import {BaseView} from "./BaseView";
import React, {useEffect} from "react";
import {usePreserveQueryParamsNavigate} from "../components/ui/lib/utils";
import axios from "../utility/customAxios";
import {useSearchParams} from "react-router-dom";
import {Button} from "../components/ui/button";
import {AlertDestinationsSelector,} from "../components/Alert/AlertDestination";
import {AlertMetricSelector} from "../components/Alert/AlertMetricSelector";
import {defaultMultiMetricAlert} from "./alerts/MultiMetricAlert";
import {AlertMultiMetricSelector} from "../components/Alert/AlertMultiMetricSelector";
import {ImportAlertDialog} from "../components/Alert/ImportAlertDialog";
import {convertToApiServerAlert, deserializeAlert} from "../components/Alert/utils";
import {AlertNameSelector} from "../components/Alert/AlertNameSelector";
import {KubernetesEventSelector} from "../components/Alert/KubernetesEventSelector";
import {AlertFilterSelector} from "../components/Alert/AlertFilterSelector";
import {LogEventSelector} from "../components/Alert/LogEventSelector";
import {AlertTypeSelector} from "../components/Alert/AlertTypeSelector";
import {Alert, AlertType} from "./alerts/model";
import {defaultLogAlert} from "./alerts/LogAlert";
import {dashboardJsonReviver} from "../components/Dashboarding/Dashboard";

function AlertCreation() {
    const [isValid, setIsValid] = React.useState<boolean>(true);
    const navigate = usePreserveQueryParamsNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showImportDialog, setShowImportDialog] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isManagedExternally, setIsManagedExternally] = React.useState<boolean>(false);
    const [alert, setAlert] = React.useState<Alert>(() => {
        let alertJson = searchParams.get("alertJson")
        if (alertJson !== null) {
            try {
                let alert = JSON.parse(alertJson, dashboardJsonReviver)
                let useableAlert = deserializeAlert(alert);
                return useableAlert
            } catch (e) {
                console.error("Failed to parse alert JSON:", e);
            }
        }

        return {
            uuid: "",
            type: AlertType.MultiMetric,
            name: "",
            description: "",
            alert: defaultMultiMetricAlert,
            destinations: []
        }
    });

    // Load the alertJson from the url param if it exists
    useEffect(() => {
        let alertJson = searchParams.get("alertJson")
        if (alertJson === null) {
            return
        }
        try {
            let alert = JSON.parse(alertJson, dashboardJsonReviver)
            let useableAlert = deserializeAlert(alert);
            setAlert(useableAlert)
        } catch (e) {
            console.error("Failed to parse alert JSON:", e);
        }
    }, [searchParams]);

    useEffect(() => {
        // Load the alert from the url id if one is set
        let alertId = searchParams.get("alertId")
        if (alertId === null) {
            return
        }
        setLoading(true);
        axios.get("/api/v1/alert?alertId=" + alertId)
            .then((response) => {
                let alert = response.data;
                let useableAlert = deserializeAlert(alert);
                setAlert(useableAlert);
                setIsManagedExternally(alert.alertManagement && alert.alertManagement.managingResourceType !== undefined);
            })
            .catch((e) => {
                console.error("Failed to fetch alert:", e);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [searchParams]);

    if (loading) {
        return (
            <BaseView title={"Edit Alert"}>
                <div className={"flex flex-col grow shrink min-h-0 min-w-0 overflow-y-auto"}>
                    <div className={"flex flex-col gap-8 m-4"}>
                        <div className={"flex justify-center text-textdark"}>
                            Loading...
                        </div>
                    </div>
                </div>
            </BaseView>
        );
    }

    return (
        <BaseView title={searchParams.get("alertId") ? "Edit Alert" : "Create Alert"}>
            <div className={"flex flex-col grow shrink min-h-0 min-w-0 overflow-y-auto"}>
                <div className={"flex flex-col gap-8 m-4"}>
                    <div className={"flex justify-between items-center"}>
                        <AlertTypeSelector alert={alert} setAlert={setAlert}/>
                        <Button
                            onClick={() => setShowImportDialog(true)}
                            className={"bg-primarytransparent border border-primary h-9 rounded"}>
                            Import JSON
                        </Button>
                    </div>
                    {alert.type === AlertType.Kubernetes &&
                        <KubernetesEventSelector alert={alert} setAlert={setAlert}/>}
                    {alert.type === AlertType.Kubernetes && <AlertFilterSelector alert={alert} setAlert={setAlert}/>}

                    {alert.type === AlertType.Log && <LogEventSelector alert={alert} setAlert={setAlert}/>}
                    {alert.type === AlertType.Log && <AlertFilterSelector alert={alert} setAlert={setAlert}/>}

                    {alert.type === AlertType.Metric && <AlertMetricSelector alert={alert} setAlert={setAlert}/>}
                    {alert.type === AlertType.Trace && <AlertMetricSelector  alert={alert} setAlert={setAlert}/>}
                    {alert.type === AlertType.KubernetesResource &&
                        <AlertMetricSelector alert={alert} setAlert={setAlert}/>}
                    {alert.type === AlertType.MultiMetric &&
                        <AlertMultiMetricSelector alert={alert} setAlert={setAlert} setIsValid={setIsValid}/>}

                    <AlertDestinationsSelector stepNumber={4} alert={alert}
                                               setAlert={setAlert}/>
                    <AlertNameSelector stepNumber={5} alert={alert}
                                       setAlert={setAlert}/>
                    <div className={"flex justify-center"}>
                        {!isManagedExternally ? (
                            <Button
                                disabled={!isValid}
                                onClick={() => {
                                    axios.post("/api/v1/alert", convertToApiServerAlert(alert))
                                        .then(() => {
                                            navigate("/alerts")
                                        })
                                        .catch((e) => {
                                            console.error("Failed to save alert:", e);
                                        });
                                }}
                                className={"bg-primarytransparent border border-primary w-full h-full rounded"}>
                                {searchParams.get("alertId") ? "Update" : "Save"}
                            </Button>
                        ) : (
                            <div className="text-textmedium text-center p-4 border border-amber-500 rounded bg-amber-500/10 w-full">
                                This alert is managed by Kubernetes ConfigMap and cannot be edited.
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ImportAlertDialog
                open={showImportDialog}
                onOpenChange={setShowImportDialog}
                onImport={(importedAlert) => {
                    setAlert(prev => {
                        return {
                            ...prev,
                            type: AlertType.Log,
                            alert: defaultLogAlert,
                        }
                    })
                    setTimeout(()=> {
                        setAlert(deserializeAlert(importedAlert));
                    }, 100)
                    setShowImportDialog(false);
                }}
            />
        </BaseView>
    );
}


export {AlertCreation}
